import {
  toWSDateString,
  useWSModalCustom,
  WSControl,
  WSElement,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSInfoBox,
  WSList,
  WSModal,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeRequirement } from "@wingspanhq/wingspan-api-sdk";
import React, { useState } from "react";

import { ErrorAlert } from "@components/ErrorAlert";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { queriesPayeeRows } from "../../../../v3/query/queriesPayeeRows";
import { queriesRequirements } from "../../../../v3/query/queriesRequirements";
import { useXWingspanPayerId } from "../../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import {
  ContextItem,
  RequirementAdditionalData,
  RequirementStatusViewProps
} from "../../types";

interface ReRequestRequirementModalProps {
  onClose: () => void;
  requirement: PayeeRequirement;
  context: ContextItem;
  additionalData: RequirementAdditionalData;
  onSuccess: () => void;
  currentStatus: RequirementStatusViewProps;
}

type FormData = {
  reason: string;
};

const ReRequestRequirementModal: React.FC<ReRequestRequirementModalProps> = ({
  onClose,
  requirement,
  context,
  onSuccess,
  currentStatus
}) => {
  const [resetRequirement, resetRequirementMeta] =
    queriesRequirements.resetPayeeRequirement.useMutation();

  const [resetExpiration, setResetExpiration] = useState(false);

  const xWingspanAccount = useXWingspanExpansion();
  const xWingspanPayerId = useXWingspanPayerId();
  const queryPayeeGeneralSummary = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: {
        requirements_requirementId: { eq: requirement.id }
      },
      page: { size: 1 },
      xWingspanAccount
    },
    { select: list => list.pagination?.totalSize }
  );

  const onSubmit = (formData: FormData) => {
    resetRequirement(
      {
        requirementId: requirement.id!,
        xWingspanAccount: xWingspanPayerId
      },
      {
        onSuccess() {
          onSuccess();
          onClose();
        }
      }
    );
  };

  const contractorsLength = queryPayeeGeneralSummary.data || "-";

  return (
    <WSForm<FormData> defaultValues={{ reason: "" }} onSubmit={onSubmit}>
      <WSModal
        actions={{
          alignment: "fill",
          buttons: [
            {
              kind: "Secondary",
              label: "Cancel",
              onClick: onClose,
              type: "button"
            },
            {
              kind: "Primary",
              label: "Yes, re-request",
              disabled: requirement.expiresAt ? !resetExpiration : false,
              type: "submit",
              loading: resetRequirementMeta.isLoading
            }
          ]
        }}
        title="Re-request requirement"
        onClose={onClose}
      >
        <WSList gap="XL">
          <WSFlexBox gap="M" wrap="nowrap">
            <WSElement>
              <WSText.ParagraphSm color="gray600" mb="S">
                Current status:
              </WSText.ParagraphSm>
              <WSPill {...currentStatus.pill}></WSPill>
              <WSText.ParagraphXs color="gray400" ml="S" mt="XS">
                {currentStatus.secondaryText}
              </WSText.ParagraphXs>
            </WSElement>
            <WSIcon
              name="arrow-right"
              style={{
                marginTop: "auto",
                marginBottom: "auto"
              }}
            />

            <WSElement>
              <WSText.ParagraphSm color="gray600" mb="S">
                Overwritten status preview:
              </WSText.ParagraphSm>
              <WSPill
                icon="time"
                text="Awaiting contractor"
                theme="neutral"
              ></WSPill>
              <WSText.ParagraphXs color="gray400" ml="S" mt="XS">
                Awaiting contractor completion
              </WSText.ParagraphXs>
            </WSElement>
          </WSFlexBox>

          {requirement.expiresAt ? (
            <WSInfoBox py="L">
              <WSList gap="M">
                <WSElement>
                  <WSText.ParagraphXs color="gray500" mb="S">
                    Current expiration date
                  </WSText.ParagraphXs>
                  <WSText.ParagraphSm color="gray600" ml="S" mt="XS">
                    {toWSDateString(requirement.expiresAt, "input")}
                  </WSText.ParagraphSm>
                </WSElement>

                <WSText.ParagraphSm color="gray600">
                  This requirement has an expiration date extracted from the
                  document used to fulfill the requirement. By re-requesting, it
                  resets the requirement completely and removes the current
                  document and its expiration date. This action cannot be
                  reverted.
                  <br />
                  <br />
                  To retain the current expiration date, click “Cancel”. The
                  contractor is automatically reminded to renew their documents
                  starting 30 days before the expiration date.
                </WSText.ParagraphSm>
                <WSControl
                  label="Yes, I want to reset the requirement entirely and ignore the previous document and its expriration date."
                  size="S"
                  type="checkbox"
                  value={resetExpiration}
                  onChange={setResetExpiration}
                />
              </WSList>
            </WSInfoBox>
          ) : null}

          {/*
          <WSList gap="L">
            <WSElement>
              <WSForm.Field
                label="Comment (optional)"
                name="reason"
                component={WSInputTextarea}
                componentProps={{
                  helperText:
                    "Please provide specific details that will inform the contractor about the rejection",
                  placeholder: "Provide specific details about the decision"
                }}
              />
            </WSElement>
          </WSList>
          */}

          <WSInfoBox>
            <WSText.ParagraphSm color="gray600">
              By proceeding, the requirement status will be reset to incomplete.
              <ul>
                <li>
                  The status will be updated to “Awaiting contractor” (requiring
                  contractor action)
                </li>
                <li>
                  Provide a reason and comments to the contractor regarding this
                  decision
                </li>
                <li>
                  The decision and status updates will be tracked in the
                  requirement history
                </li>
              </ul>
            </WSText.ParagraphSm>
            <WSText.ParagraphSm color="gray600">
              This requirement is assigned to {contractorsLength} of the
              contractor’s engagements and may affect their eligibility to
              receive payments from you. Would you like to proceed?
            </WSText.ParagraphSm>
          </WSInfoBox>
          <ErrorAlert
            context="Other"
            error={resetRequirementMeta.isError}
            mt="XL"
          />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useReRequestRequirementModal = () =>
  useWSModalCustom(ReRequestRequirementModal);
